import { useRef, type ReactElement } from 'react';
import {
  ChevronRight,
  ListTree,
  PanelRightClose,
  PanelRightOpen,
  Box,
  Braces,
  Hash,
  Type as TypeIcon,
  Boxes,
  SquareFunction,
  Variable,
} from 'lucide-react';
import type { CodeSymbol, SymbolKind } from '@slate/core';
import { useRegisterFocusZone } from '../focus';
import { useOutlineSpeedSearch, type OutlineRow } from './useOutlineSpeedSearch';
import { HighlightedLabel } from './HighlightedLabel';

export interface CodeOutlinePanelProps {
  symbols: CodeSymbol[];
  pinned: boolean;
  onTogglePin: () => void;
  /** Jump the editor to a symbol's 1-based start line. */
  onJump: (line: number) => void;
}

const KIND_ICON: Record<SymbolKind, typeof Box> = {
  class: Box,
  interface: Braces,
  enum: Hash,
  type: TypeIcon,
  namespace: Boxes,
  function: SquareFunction,
  method: Variable,
};

/** Stable per-document id for a symbol row. */
const symbolId = (s: CodeSymbol): string => `${s.name}:${s.line}`;

interface RowProps {
  row: OutlineRow<CodeSymbol>;
  onJump: (line: number) => void;
  onToggle: (id: string) => void;
}

function CodeOutlineRow({ row, onJump, onToggle }: RowProps): ReactElement {
  const { node, id, level, hasChildren, open, isMatch, isFocused, range } = row;
  const Icon = KIND_ICON[node.kind];
  return (
    <div
      className={'mp-outline__row' + (isMatch ? ' mp-outline__row--match' : '')}
      data-outline-id={id}
      data-focused={isFocused ? '' : undefined}
      role="treeitem"
      aria-expanded={hasChildren ? open : undefined}
      aria-level={level + 1}
      aria-selected={isFocused ? true : undefined}
      tabIndex={0}
      style={{ paddingLeft: `${8 + level * 14}px` }}
      title={`${node.name} (${node.kind})`}
      onClick={() => onJump(node.line)}
    >
      {hasChildren ? (
        <button
          type="button"
          className="mp-outline__chevron"
          data-open={open ? '' : undefined}
          aria-label={open ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(id);
          }}
        >
          <ChevronRight size={13} aria-hidden strokeWidth={2.25} />
        </button>
      ) : (
        <span className="mp-outline__chevron-gap" aria-hidden />
      )}
      <Icon size={13} aria-hidden className="mp-outline__kind-icon" data-kind={node.kind} />
      <span className="mp-outline__label">
        <HighlightedLabel text={node.name} range={range} />
      </span>
    </div>
  );
}

/**
 * Right-edge outline for code files: a hierarchy of class/function/method
 * definitions with IntelliJ-style speed search (type to highlight + cycle).
 */
export function CodeOutlinePanel({
  symbols,
  pinned,
  onTogglePin,
  onJump,
}: CodeOutlinePanelProps): ReactElement {
  const outlineRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  useRegisterFocusZone(outlineRef, 'outline', '[data-focus-primary], button', {
    label: 'Outline',
    order: 25,
    isVisible: () => true,
  });

  const speed = useOutlineSpeedSearch<CodeSymbol>({
    roots: symbols,
    getId: symbolId,
    getLabel: (s) => s.name,
    getChildren: (s) => s.children,
    onActivate: (s) => onJump(s.line),
    bodyRef,
  });

  return (
    <div
      className="mp-outline__inner"
      role="tree"
      aria-label="Code outline"
      data-focus-zone="outline"
      ref={outlineRef}
    >
      <div className="mp-outline__header">
        <span className="mp-outline__title">
          <ListTree size={14} aria-hidden strokeWidth={2} />
          Outline
        </span>
        <button
          type="button"
          className="mp-iconbtn"
          aria-pressed={pinned}
          aria-label={pinned ? 'Unpin outline' : 'Pin outline'}
          title={pinned ? 'Unpin outline' : 'Pin outline'}
          onClick={onTogglePin}
        >
          {pinned ? <PanelRightClose size={15} aria-hidden /> : <PanelRightOpen size={15} aria-hidden />}
        </button>
      </div>
      <div
        ref={bodyRef}
        className="mp-outline__body"
        tabIndex={0}
        onKeyDown={speed.handleKeyDown}
        onBlur={speed.onBlur}
      >
        {speed.active && (
          <div
            className="mp-outline__speedsearch"
            data-no-match={speed.matchCount === 0 ? '' : undefined}
            role="status"
            aria-live="polite"
          >
            {speed.query}
          </div>
        )}
        {symbols.length === 0 ? (
          <p className="mp-outline__empty">No symbols</p>
        ) : (
          speed.rows.map((row) => (
            <CodeOutlineRow
              key={row.id}
              row={row}
              onJump={onJump}
              onToggle={speed.toggleCollapse}
            />
          ))
        )}
      </div>
    </div>
  );
}
